/*
 * Pure data builders for the Visão Geral "Position" card. No React, no i18n —
 * just the shaping needed for the depth-occupancy donut and the team-formation
 * head-to-head, so the values are unit-testable in the node test environment.
 */
import type { SegmentStatistics } from '@/types/statistics';
import { largestRemainderPercents, REPORT_SKIN } from '@/lib/utils';

type FrontBack = { net: number; transition: number; back: number };
type SegStats = SegmentStatistics['statistics'];

export type DepthKey = 'net' | 'transition' | 'back';

// Depth ramp (front → back): brighter sky at the net, deepening to navy at the
// back wall, so the donut reads as court depth. Not theme tokens — there's no
// 3-step scale in the palette — but tuned to sit on the light card surface.
// Spaced sky-400 / sky-600 / sky-900 so the three bands stay clearly distinct
// (transition vs back were too close on adjacent shades). Shared by the Visão
// Geral Position card and the In-Depth Movement tab. The Matchlytics skin runs
// the same light → dark ramp in grays.
export const DEPTH_COLORS: Record<DepthKey, string> =
  REPORT_SKIN === 'matchlytics'
    ? { net: '#a3a3a3', transition: '#525252', back: '#0a0a0a' }
    : {
        net: '#38bdf8', // sky-400 — at the net
        transition: '#0284c7', // sky-600 — the middle / transition band
        back: '#0c4a6e', // sky-900 — at the back wall
      };

export interface DepthSlice {
  key: DepthKey;
  /** Raw occupancy ratio as stored. */
  value: number;
  /** Whole-percent share — the three slices always sum to exactly 100. */
  pct: number;
}

const DEPTH_ORDER: DepthKey[] = ['net', 'transition', 'back'];

/**
 * Net / transition (middle) / back depth occupancy as whole percentages
 * (front-of-court → back). Allocated with the largest-remainder method so the
 * three always sum to exactly 100 (the donut closes and the legend never reads
 * 99%/101%). Returns null when there's no occupancy to draw (all zero).
 */
export function depthOccupancy(fb: FrontBack): DepthSlice[] | null {
  const raw = DEPTH_ORDER.map((k) => fb[k] ?? 0);
  if (raw.reduce((s, v) => s + v, 0) <= 0) return null;
  const pcts = largestRemainderPercents(raw);
  return DEPTH_ORDER.map((key, i) => ({ key, value: raw[i], pct: pcts[i] }));
}

/** One team's side of the head-to-head, as stored (ratios stay 0..1, the two
    durations stay in seconds — the card formats them). */
export interface TeamFormationSide {
  sync: number;
  coverage: number;
  /** Share of rally time the pair held the net. */
  netTime: number;
  /** Times per rally they moved up and took it. */
  conquests: number;
  /** Seconds they kept it once taken. */
  hold: number;
  /** Seconds taken to get from the back up to the net. */
  advance: number;
}

export interface TeamFormationVersus {
  your: TeamFormationSide;
  opp: TeamFormationSide;
}

/**
 * The focus player's team vs the opponents on net dominance, depth
 * synchronization and lateral (court) coverage, read from formation_analysis.
 * Returns null when formation data is absent (e.g. basic reports) or either
 * team can't be resolved.
 */
export function resolveTeamFormationVersus(
  teams: SegStats['teams'],
  formationAnalysis: SegStats['formation_analysis'],
  focusPlayerId: string,
): TeamFormationVersus | null {
  if (!formationAnalysis) return null;
  const teamIds = Object.keys(teams);
  const yourTeamId = teamIds.find(
    (id) => teams[id]?.teammates?.includes(focusPlayerId),
  );
  const oppTeamId = teamIds.find((id) => id !== yourTeamId);
  if (!yourTeamId || !oppTeamId) return null;

  const side = (teamId: string): TeamFormationSide | null => {
    const fa = formationAnalysis[teamId]?.formation_analysis;
    const s = fa?.team_synchronization;
    const n = fa?.net_dominance;
    if (!s || !n) return null;
    return {
      sync: s.depth_sync_score,
      coverage: s.lateral_coverage_ratio,
      netTime: n.net_time_ratio,
      conquests: n.net_conquests_per_rally,
      hold: n.avg_net_hold_duration,
      advance: n.avg_back_to_net_duration,
    };
  };

  const your = side(yourTeamId);
  const opp = side(oppTeamId);
  if (!your || !opp) return null;

  return { your, opp };
}
